import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMarket } from '../hooks/useSDI'
import { useAISDIInsight } from '../hooks/useAISDIInsight'
import { MarketTabsLayout } from '../components/sdi/shared/MarketTabsLayout'
import { KpiCard } from '../components/sdi/shared/KpiCard'
import type { MarketOverview, ConnectorStatus } from '../hooks/sdi.types'
import {
  BarChart3, DollarSign, Eye, TrendingDown,
  AlertTriangle, Lightbulb, ChevronRight, Shield, Info,
  RefreshCw, Download, Copy, Loader2, Upload,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ReferenceLine, Brush, Cell,
} from 'recharts'

import { useImportCsv } from '../hooks/useSDI'
import { CsvImportModal } from '../components/sdi/markets/CsvImportModal'

const fmtCompact = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 })
const fmtPct = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1 })
const fmtEur = new Intl.NumberFormat('fr-FR', { notation: 'compact', style: 'currency', currency: 'EUR', maximumFractionDigits: 1 })

// ── InsightCard (AI) ────────────────────────────────────

function InsightCard({ market }: { market: MarketOverview }) {
  const ai = useAISDIInsight()

  const handleGenerate = () => {
    ai.generate({
      market_name: market.name,
      market_size: market.market_size ?? 0,
      capture_rate: market.capture_rate ?? 0,
      budget_current: 0,
      budget_linear: market.budget_reco ?? 0,
      capture_elastic: market.capture_rate ?? 0,
      top_opportunities: [],
    })
  }

  if (!ai.insight && !ai.isLoading) {
    return (
      <div className="bg-onyx rounded-card p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 44 44" fill="none">
              <circle cx="22" cy="22" r="8" stroke="#0F7173" strokeWidth="1.5" fill="none"/>
              <circle cx="22" cy="22" r="14" stroke="#0F7173" strokeWidth="1" opacity="0.4" fill="none"/>
              <circle cx="22" cy="22" r="20" stroke="#0F7173" strokeWidth="0.7" opacity="0.2" fill="none"/>
              <circle cx="22" cy="22" r="3" fill="#C9A84C"/>
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-xl text-paper font-bold">Analyse IA du marché</h3>
            <p className="text-[11px] text-paper/40 font-sans mt-1">Synthèse exécutive générée par Claude Sonnet</p>
          </div>
          <button onClick={handleGenerate} disabled={ai.isLoading}
            className="flex items-center gap-2 bg-teal text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-teal/90 transition-colors disabled:opacity-50">
            {ai.isLoading ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
            Générer l'analyse
          </button>
        </div>
        {ai.error && <p className="text-xs text-brick mt-3">{ai.error}</p>}
      </div>
    )
  }

  if (ai.isLoading) {
    return (
      <div className="bg-onyx rounded-card p-6 animate-pulse">
        <div className="h-6 w-48 bg-paper/10 rounded mb-4" />
        <div className="h-4 w-full bg-paper/10 rounded mb-2" />
        <div className="h-4 w-3/4 bg-paper/10 rounded mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-16 bg-paper/10 rounded" />
          <div className="h-16 bg-paper/10 rounded" />
        </div>
      </div>
    )
  }

  const ins = ai.insight!

  return (
    <div className="bg-onyx rounded-card p-6 animate-fade-in">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-4">
        <span className="bg-teal/15 text-teal text-[10px] font-medium px-2 py-0.5 rounded-full">Claude Sonnet</span>
        {ai.lastGenerated && (
          <span className="text-paper/30 text-[10px]">
            Généré le {ai.lastGenerated.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <button onClick={() => { ai.reset(); handleGenerate() }}
          className="text-paper/40 hover:text-paper/70 text-[11px] flex items-center gap-1 ml-auto transition-colors">
          <RefreshCw size={12} /> Régénérer
        </button>
      </div>

      {/* Summary */}
      <div className="border-l-2 border-teal pl-4 mb-5">
        <p className="font-serif text-[15px] font-light italic text-paper/85 leading-relaxed">
          {ins.summary}
        </p>
      </div>

      {/* Grid: Opportunity + Budget */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        <div>
          <p className="text-teal/60 text-[9px] uppercase tracking-widest mb-1">Opportunité</p>
          <p className="font-sans text-[12px] text-paper/65 leading-relaxed">{ins.opportunity_text}</p>
        </div>
        <div>
          <p className="text-gold/60 text-[9px] uppercase tracking-widest mb-1">Recommandation budget</p>
          <p className="font-sans text-[12px] text-paper/65 leading-relaxed">{ins.budget_recommendation}</p>
        </div>
      </div>

      {/* Risks */}
      <div>
        <p className="text-[10px] text-paper/40 uppercase tracking-wider mb-2">Points de vigilance</p>
        <div className="space-y-1.5">
          {ins.risks.map((risk, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-b-[6px] border-b-brick mt-1 flex-shrink-0" />
              <p className="text-[11px] text-paper/55">{risk}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Seasonality AreaChart with gradient ───────────────────────

function SeasonalityAreaChart({ period }: { period: string }) {
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
  const data = months.map((m, i) => ({
    month: m,
    index: parseFloat((0.8 + Math.sin((i / 12) * Math.PI * 2) * 0.3 + Math.random() * 0.1).toFixed(2)),
  }))
  const sliceEnd = period === '3m' ? 3 : period === '6m' ? 6 : 12

  return (
    <div className="bg-white rounded-xl border border-surface-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-surface-700">Saisonnalité</h3>
        <span className="text-xs text-surface-400 flex items-center gap-1"><Info size={12} /> Indice relatif</span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data.slice(0, sliceEnd)}>
          <defs>
            <linearGradient id="seasonGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0F7173" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#0F7173" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
          <YAxis domain={[0.4, 1.6]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
          <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="5 5" />
          <Tooltip formatter={(v: number) => v.toFixed(2)} contentStyle={{ borderRadius: '0.75rem', border: '1px solid #e2e8f0', fontSize: 12 }} />
          <Area type="monotone" dataKey="index" stroke="#0F7173" strokeWidth={2} fill="url(#seasonGrad)" dot={{ fill: '#0F7173', r: 3 }} activeDot={{ r: 5 }} />
          {data.length > 6 && <Brush dataKey="month" height={20} stroke="#e2e8f0" />}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── Demand vs Captured stacked bar ───────────────────────────

function DemandVsCapturedStacked({ marketSize, captured }: { marketSize: number | null; captured: number | null }) {
  if (!marketSize || !captured) return null
  const lost = marketSize - captured
  const data = [
    { name: 'Capturé', value: captured },
    { name: 'Non capturé', value: lost },
  ]

  return (
    <div className="bg-white rounded-xl border border-surface-200 p-5">
      <h3 className="text-sm font-semibold text-surface-700 mb-4">Demande capturée vs. totale</h3>
      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={data} layout="vertical" barSize={28}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12, fill: '#64748b' }} />
          <Tooltip formatter={(v: number) => fmtCompact.format(v)} contentStyle={{ borderRadius: '0.75rem', border: '1px solid #e2e8f0', fontSize: 12 }} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]}>
            <Cell fill="#0F7173" />
            <Cell fill="#e2e8f0" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-4 mt-3 text-xs text-surface-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-teal" /> Capturé: {fmtCompact.format(captured)} ({fmtPct.format(captured / marketSize)})</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-surface-200" /> Perdu: {fmtCompact.format(lost)}</span>
      </div>
    </div>
  )
}

// ── Enhanced Auto Insights ───────────────────────────────────

function EnhancedInsights({ market }: { market: MarketOverview }) {
  const navigate = useNavigate()
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)

  type Severity = 'urgent' | 'attention' | 'good'
  const insights: { severity: Severity; text: string; detail?: string; cta_label?: string; cta_route?: string }[] = []

  if (market.capture_rate != null && market.capture_rate < 0.15) {
    insights.push({ severity: 'urgent', text: `Capture critique à ${fmtPct.format(market.capture_rate)} — bien en dessous du seuil de rentabilité.`,
      detail: 'Le taux de capture est calculé via le Search Impression Share.',
      cta_label: 'Simuler un budget', cta_route: `/sdi/markets/${market.id}/budget?target_capture=0.25` })
  } else if (market.capture_rate != null && market.capture_rate < 0.25) {
    insights.push({ severity: 'attention', text: `Capture à ${fmtPct.format(market.capture_rate)} — sous la moyenne sectorielle de 25%.`,
      cta_label: 'Simuler un budget', cta_route: `/sdi/markets/${market.id}/budget` })
  }

  if (market.lost_value != null && market.lost_value > 50000) {
    insights.push({ severity: 'urgent', text: `${fmtEur.format(market.lost_value)} d'opportunité non capturée.`,
      cta_label: 'Voir les opportunités', cta_route: `/sdi/markets/${market.id}/opportunities` })
  }

  if (market.keyword_count < 10) {
    insights.push({ severity: 'attention', text: `Cluster de ${market.keyword_count} mot(s)-clé(s) — enrichissez pour une vision complète.`,
      cta_label: 'Gérer les mots-clés', cta_route: `/sdi/markets/${market.id}/keywords` })
  }

  if (insights.length === 0) {
    insights.push({ severity: 'good', text: 'Tout semble en ordre. Les données sont à jour et le cluster est bien couvert.' })
  }

  insights.sort((a, b) => {
    const order: Record<Severity, number> = { urgent: 0, attention: 1, good: 2 }
    return order[a.severity] - order[b.severity]
  })

  const severityConfig: Record<Severity, { dot: string; bg: string }> = {
    urgent: { dot: 'bg-red-500', bg: 'border-l-red-500 bg-red-50/30' },
    attention: { dot: 'bg-amber-500', bg: 'border-l-amber-500 bg-amber-50/30' },
    good: { dot: 'bg-emerald-500', bg: 'border-l-emerald-500 bg-emerald-50/30' },
  }

  return (
    <div className="bg-white rounded-xl border border-surface-200 p-5">
      <h3 className="text-sm font-semibold text-surface-700 mb-4">Insights automatiques</h3>
      <div className="space-y-3">
        {insights.slice(0, 5).map((ins, i) => {
          const cfg = severityConfig[ins.severity]
          return (
            <div key={i} className={cn('border-l-4 rounded-lg px-4 py-3 cursor-pointer', cfg.bg)} onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}>
              <div className="flex items-start gap-3">
                <span className={cn('w-2 h-2 rounded-full mt-1.5 flex-shrink-0', cfg.dot)} />
                <div className="flex-1">
                  <p className="text-sm text-surface-700">{ins.text}</p>
                  {expandedIdx === i && ins.detail && (
                    <p className="text-xs text-surface-400 mt-2 font-mono bg-surface-50 rounded px-2 py-1">{ins.detail}</p>
                  )}
                  {ins.cta_label && ins.cta_route && (
                    <button onClick={(e) => { e.stopPropagation(); navigate(ins.cta_route!) }}
                      className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-teal hover:text-teal/80">
                      {ins.cta_label} <ChevronRight size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────

export function MarketOverviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: market, isLoading, refetch } = useMarket(id!)
  const [chartPeriod, setChartPeriod] = useState('12m')
  const [showCsvImport, setShowCsvImport] = useState(false)
  const importCsv = useImportCsv()

  // Layout handles loading & not-found states, but we still
  // need market data for the page content below
  if (isLoading || !market) {
    return (
      <MarketTabsLayout>
        <div className="space-y-6 animate-pulse">
          <div className="grid grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 bg-surface-100 rounded-xl" />)}</div>
          <div className="h-64 bg-surface-100 rounded-xl" />
        </div>
      </MarketTabsLayout>
    )
  }

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href)
  }

  return (
    <MarketTabsLayout>
      {/* Action buttons row */}
      <div className="flex items-center justify-end gap-2">
        <button onClick={() => refetch()} className="p-2 rounded-lg text-surface-400 hover:text-teal hover:bg-teal/10" title="Relancer sync">
          <RefreshCw size={18} />
        </button>
        <button onClick={handleShare} className="p-2 rounded-lg text-surface-400 hover:text-teal hover:bg-teal/10" title="Partager">
          <Copy size={18} />
        </button>
        <button className="p-2 rounded-lg text-surface-400 hover:text-teal hover:bg-teal/10" title="Exporter PDF">
          <Download size={18} />
        </button>
        <button onClick={() => setShowCsvImport(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-teal bg-teal/10 hover:bg-teal/20 transition-colors" title="Importer CSV">
          <Upload size={16} /> Import CSV
        </button>
      </div>

      {/* Stale Banner */}
      {market.stale && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">Données de plus de 14 jours — relancez une synchronisation.</p>
        </div>
      )}

      {/* ══ AI INSIGHT CARD ═══════════════════════════ */}
      <InsightCard market={market} />

      {/* KPI Strip — 5 clickable cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard label="Taille du marché" value={market.market_size ? fmtCompact.format(market.market_size) : '—'} icon={BarChart3}
          color="text-teal bg-teal/10" drillDown={{ title: 'Taille du marché — Détail', subtitle: `${fmtCompact.format(market.market_size ?? 0)} recherches/mois` }} />
        <KpiCard label="Valeur du marché" value={market.market_value ? fmtEur.format(market.market_value) : '—'} icon={DollarSign}
          color="text-violet bg-violet/10" drillDown={{ title: 'Valeur du marché', subtitle: 'market_size × avg_cpc_high' }} />
        <KpiCard label="Taux de capture" value={market.capture_rate ? fmtPct.format(market.capture_rate) : '—'} icon={Eye}
          color="text-emerald-600 bg-emerald-50" drillDown={{ title: 'Taux de capture — Détail' }} />
        <KpiCard label="Opportunité perdue" value={market.lost_value ? fmtEur.format(market.lost_value) : '—'} icon={TrendingDown}
          color="text-brick bg-brick-light" drillDown={{ title: 'Opportunité perdue — Détail' }} />
        <KpiCard label="Confiance" value={market.confidence_score ? `${market.confidence_score.toFixed(0)}/100` : '—'} icon={Shield}
          color="text-violet bg-violet/10" drillDown={{ title: 'Score de confiance — Détail' }} />
      </div>

      {/* Chart period toggle */}
      <div className="flex items-center gap-1">
        {['3m', '6m', '12m'].map((p) => (
          <button key={p} onClick={() => setChartPeriod(p)}
            className={cn('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              chartPeriod === p ? 'bg-teal text-white' : 'bg-surface-100 text-surface-600 hover:bg-surface-200')}>
            {p === '3m' ? '3 mois' : p === '6m' ? '6 mois' : '12 mois'}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DemandVsCapturedStacked marketSize={market.market_size} captured={market.captured_demand} />
        <SeasonalityAreaChart period={chartPeriod} />
      </div>

      {/* Enhanced Insights */}
      <EnhancedInsights market={market} />

      {/* Connectors Status */}
      {market.connectors.length > 0 && (
        <div className="bg-white rounded-xl border border-surface-200 p-5">
          <h3 className="text-sm font-semibold text-surface-700 mb-4">Connecteurs</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {market.connectors.map((c: ConnectorStatus) => {
              const isOk = c.status === 'connected'
              return (
                <div key={c.source} className={cn('rounded-lg border p-3', isOk ? 'border-emerald-200 bg-emerald-50/30' : 'border-surface-200 bg-surface-50')}>
                  <p className="text-xs font-medium text-surface-700">{c.source.replace(/_/g, ' ')}</p>
                  <p className={cn('text-xs mt-1 font-semibold', isOk ? 'text-emerald-600' : c.status === 'error' ? 'text-red-600' : 'text-surface-400')}>{c.status}</p>
                  {c.last_sync_at && <p className="text-[10px] text-surface-400 mt-1">Sync: {new Date(c.last_sync_at).toLocaleDateString('fr-FR')}</p>}
                </div>
              )
            })}
          </div>
        </div>
      )}
      {/* CSV Import Modal */}
      <CsvImportModal
        open={showCsvImport}
        onClose={() => setShowCsvImport(false)}
        onImport={async (file) => {
          await importCsv.mutateAsync({ marketId: id!, file })
        }}
        isImporting={importCsv.isPending}
      />
    </MarketTabsLayout>
  )
}
